import { Book, Language, Sentence, copyRestObject } from "../../../../rest/models";
import { getModelMap } from "../../../../rest/slice";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { AppDispatch } from "../../../../store/store";
import { InfoTrigger } from "../../../info_popup/InfoTrigger";
import { Button } from "../../../RequestRespectingInputs";
import { renumberBlocksAndSentences } from "../../utils";
import { bookReaderActions } from "../slice";
import { EWRTranslationEditor } from "./EWRTranslationEditor";

export const EWRSentenceTranslationTextBoxClass = 'ernots_EWRSentenceTranslationTextBox';
const containerClass = 'ernots_EWRSentenceEditor';

/**
 * Below, I need to know whether a text box had focus when a button was clicked...
 * But by that time, it's the button that has focus.
 * 
 * So I capture whether the element that had focus just before the button was the text box.
 */
let focusedElementHistory = [] as (EventTarget | null)[];
document.addEventListener('focusin', function(event)
{
    focusedElementHistory.push(event.target);
    focusedElementHistory = focusedElementHistory.slice(-2);
});


export function EWRSentenceEditor(props: Props)
{
    const appDispatch = useAppDispatch();
    const langModels = getModelMap(useAppSelector, Language);

    const sen = props.sentence;

    const book = useAppSelector((state) => state.bookReader.book);
    const bookLangs = useAppSelector((state) => state.bookReader.bookLangs);
    
    if (!sen)
    {
        return <></>;
    }
    else if (sen.type === 'n' && sen.html)
    {
        //It's a placeholder sentence for a non-text element.
        return <></>;

    }
    else
    {
        const tlElms = bookLangs.map(function(bl)
        {
            const l = langModels?.[bl.language];
            if (!l) return <></>;

            return <EWRTranslationEditor key={bl.key} booklanguage_id={bl.key} language={l} sentence={sen}/>
        });

        return (
        <div className={containerClass}>
            {tlElms}
            <fieldset>
                <Button onClick={e => addSentence(book as Book, sen, appDispatch)}>Add sentence</Button>
                <> </>
                <Button onClick={e => deleteSentence(book as Book, sen, appDispatch)}>Delete sentence</Button>
                <> </>
                <span>
                    <Button onClick={e => mergeWithNext(book as Book, sen, appDispatch)}>Merge with next</Button>
                    <InfoTrigger children="Useful if a sentence was split where it shouldn't have been. Merges this sentence (n) with the one after it (n+1). The translations in n+1 are appended to their counterparts in n, and then n+1 is deleted."/>
                </span>
                <> </>
                <span>
                    <Button onClick={e => splitAtCursor(book as Book, e.currentTarget, sen, appDispatch)}>Split at cursor</Button>
                    <InfoTrigger children={
                    `
                        Useful if a sentence wasn't split as it should've been. Place the cursor in the textbox for the source language, then the button. A new sentence gets added, containing text after the cursor.
                        <br/><br/>
                        Only the source text gets split. The rest, you have to adjust manually.
                        The "Open Translation" button opens Google Translate with the new sentences.
                        (Note that their interface has a button to easily copy the translation.)
                    `}/>
                </span>
            </fieldset>
        </div>);
    }
}

interface Props
{
    sentence: Sentence;
}

function addSentence(book: Book, currentSentence: Sentence, appDispatch: AppDispatch)
{
    book = JSON.parse(JSON.stringify(book));
    
    const newSentence = getNewSentence(currentSentence, '');
    spliceSentence(book, currentSentence.sentence_id + 1, 0, appDispatch, newSentence);
}

function deleteSentence(book: Book, currentSentence: Sentence, appDispatch: AppDispatch)
{
    if (window.confirm('Delete sentence?'))
    {
        spliceSentence(book, currentSentence.sentence_id, 1, appDispatch);
    }
}

function splitAtCursor(book: Book, sender: HTMLElement, currentSentence: Sentence, appDispatch: AppDispatch)
{
    let container: HTMLElement|null = sender;
    while (container && !container.classList.contains(containerClass))
    {
        container = container.parentElement;
    }
    if (!container) return;

    const textbox = container.querySelector(`input[type=text].${EWRSentenceTranslationTextBoxClass}[data-language_id='${book.source_language}']`) as HTMLInputElement;
    if (!focusedElementHistory.includes(textbox))
    {
        alert("To use this function, you must place the cursor inside the text box for this sentence's source language.");
        return;
    }

    const cursorPosition = textbox.selectionStart ?? 0;
    const textBefore = textbox.value.substring(0, cursorPosition);
    const textAfter = textbox.value.substring(cursorPosition);

    const textBeforeSentence = getNewSentence(currentSentence, textBefore);
    book = spliceSentence(book, currentSentence.sentence_id, 1, appDispatch, textBeforeSentence);
    
    const textAfterSentence = getNewSentence(currentSentence, textAfter);
    book = spliceSentence(book, currentSentence.sentence_id + 1, 0, appDispatch, textAfterSentence);
}

function mergeWithNext(book: Book, currentSentence: Sentence, appDispatch: AppDispatch)
{
    const nextSentenceText = book?.sentence_set?.[currentSentence.sentence_id + 1]?.text;
    if (nextSentenceText == null) return;

    const newText = currentSentence.text + nextSentenceText;
    const newSentence = getNewSentence(currentSentence, newText);
    
    spliceSentence(book, currentSentence.sentence_id, 2, appDispatch, newSentence);
}

function spliceSentence(book: Book, start: number, deleteCount: number, appDispatch: AppDispatch, newSentence?: Sentence)
{
    book = JSON.parse(JSON.stringify(book));
    const arrNewSentence = newSentence ? [newSentence] : [];

    book.sentence_set.splice(start, deleteCount, ...arrNewSentence);

    for (const bl of book.booklanguage_set)
    {
        const blsl = bl.language === book.source_language;
        const arrNewText = arrNewSentence.map(s => (blsl && s.text) ? s.text : '');

        const arr = bl.translation.split('\n');
        arr.splice(start, deleteCount, ...arrNewText);
        bl.translation = arr.join('\n');
    }
    
    renumberBlocksAndSentences(book);

    book.unsaved_changes = true;
    appDispatch(bookReaderActions.setBook(book));

    return book;
}

function getNewSentence(oldSentence: Sentence, newText: string)
{
    const newSentence = copyRestObject(new Sentence(), Sentence);
    newSentence.parent_block_id = oldSentence.parent_block_id;
    newSentence.type = oldSentence.type;
    newSentence.position = oldSentence.position + 0.1;
    newSentence.text = newText;

    return newSentence;
}